import { v4 as uuidv4 } from 'uuid';

import queryCache from 'data/cache';
import { Entity, EntityStatus } from 'data/types/menu';
import {
  MenuLocal,
  MenuStatus,
  Category,
  ModalItem,
  ModalModifier,
  ModifiersGroup,
  Item,
} from 'model/menu';
import move from 'utils/move';
import { difference } from 'lodash';

const updateStatus = (status: EntityStatus) =>
  status === 'EXISTING' ? 'UPDATED' : status;

export const openCategoryModal = (brandId: string, category?: Category) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const menuUpdated = {
      ...cachedMenu,
      modal: {
        category: {
          previousData: category,
        },
        item: undefined,
      },
    };

    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};
export const addCategory = (brandId: string, data: Partial<Entity>) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const newCategory = {
      id: uuidv4(),
      name: data.name,
      type: 'CATEGORY',
      status: 'NEW',
      childrenEntities: [],
    };
    const menuUpdated = {
      ...cachedMenu,
      structure: [...cachedMenu.structure, newCategory],
      hasChanges: true,
    };

    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};
export const updateCategory = (brandId: string, data: Partial<Entity>) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const menuUpdated = {
      ...cachedMenu,
      structure: cachedMenu.structure.map((category) =>
        category.id === data.id
          ? {
              ...category,
              name: data.name,
              status: updateStatus(category.status),
            }
          : category,
      ),
      hasChanges: true,
    };

    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};

type MoveCategoryProps = { from: number; to: number; brandId: string };
export const moveCategory = ({ from, to, brandId }: MoveCategoryProps) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);
  if (cachedMenu) {
    const updatedStructure: Entity[] = move({
      arr: cachedMenu.structure,
      from,
      to,
      isMutate: false,
    });

    updatedStructure[from].status = updateStatus(updatedStructure[from].status);
    updatedStructure[to].status = updateStatus(updatedStructure[to].status);

    queryCache.setQueryData(menuQuery, {
      ...cachedMenu,
      structure: updatedStructure,
      hasChanges: true,
    });
  }
};

export const removeCategory = (brandId: string, data: Partial<Entity>) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);
  if (cachedMenu) {
    const structureUpdated: Entity[] = [];
    cachedMenu.structure.forEach((category) => {
      if (category.id !== data.id) {
        structureUpdated.push(category);
      }
    });

    queryCache.setQueryData(menuQuery, {
      ...cachedMenu,
      structure: structureUpdated,
      hasChanges: true,
    });
  }
};

type ItemModalProps = {
  brandId: string;
  item: Partial<ModalItem>;
};
export const openItemModal = ({ brandId, item }: ItemModalProps) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const menuUpdated = {
      ...cachedMenu,
      modal: {
        item: {
          previousData: item,
          categoryOptions: cachedMenu.structure.map((category) => ({
            value: category.id,
            label: category.name,
          })),
        },
      },
    };

    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};
export const addItem = (
  brandId: string,
  item: Partial<ModalItem>,
  newItemId: string,
) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const newItem = {
      itemId: newItemId,
      name: item.name,
      description: item.description,
      printDescription: item.printDescription,
      price: item.price,
      status: item.status,
      state: 'PROCESSING',
      imageUrl: item.imageUrl,
      imageUrlSmall: item.imageUrlSmall,
      imageUrlMedium: item.imageUrlMedium,
    };
    const newItemEntity = {
      name: item.name?.displayName,
      id: newItem.itemId,
      type: 'ITEM',
      status: 'NEW',
      childrenEntities: item.childrenEntities,
    };
    let updatedModifierGroups = {
      ...cachedMenu.modifierGroups,
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    item.childrenEntities?.forEach((entity) => {
      updatedModifierGroups = {
        ...updatedModifierGroups,
        [entity.id]: {
          ...updatedModifierGroups[entity.id],
          usedIn: [...updatedModifierGroups[entity.id].usedIn, newItem.itemId],
        },
      };
    });

    const menuUpdated = {
      ...cachedMenu,
      structure: cachedMenu.structure.map((category) =>
        category.id === item.categoryId
          ? {
              ...category,
              childrenEntities: [...category.childrenEntities, newItemEntity],
              status: updateStatus(category.status),
            }
          : category,
      ),
      items: {
        ...cachedMenu.items,
        [newItem.itemId]: newItem,
      },
      modifiersGroup: updatedModifierGroups,
      hasChanges: true,
    };

    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};
export const updateItemImage = (
  brandId: string,
  itemId: string,
  imageUrl: string,
  imageUrlSmall: string,
  imageUrlMedium: string,
) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu == null) {
    return;
  }

  const item = cachedMenu.items[itemId];

  if (item == null) {
    return;
  }
  cachedMenu.items[itemId] = {
    ...item,
    imageUrl,
    imageUrlSmall,
    imageUrlMedium,
  };

  queryCache.setQueryData(menuQuery, { ...cachedMenu });
};
export const updateItem = (
  brandId: string,
  item: ModalItem,
  previousCategoryId: string,
  deletedItemModifiers: string[],
) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);
  const categoryHasChanged = previousCategoryId !== item.categoryId;

  if (cachedMenu) {
    const updatedItem = {
      itemId: item.itemId,
      name: item.name,
      description: item.description,
      printDescription: item.printDescription,
      price: item.price,
      status: item.status,
      imageUrl: item.imageUrl,
      imageUrlSmall: item.imageUrlSmall,
      imageUrlMedium: item.imageUrlMedium,
    };
    const itemsUpdated = {
      ...cachedMenu.items,
      [updatedItem.itemId]: updatedItem,
    };
    let modifiersUpdated = {
      ...cachedMenu.modifierGroups,
    };

    item.childrenEntities.forEach((itemModifier) => {
      const currentUsedIn = modifiersUpdated[itemModifier.id].usedIn;
      const alreadyIncluded = currentUsedIn.includes(item.itemId);

      modifiersUpdated = {
        ...modifiersUpdated,
        [itemModifier.id]: {
          ...modifiersUpdated[itemModifier.id],
          usedIn: alreadyIncluded
            ? currentUsedIn
            : currentUsedIn.concat(item.itemId),
        },
      };
    });
    deletedItemModifiers.forEach((modifiersId) => {
      modifiersUpdated = {
        ...modifiersUpdated,
        [modifiersId]: {
          ...modifiersUpdated[modifiersId],
          usedIn: modifiersUpdated[modifiersId].usedIn.filter(
            (id) => item.itemId !== id,
          ),
        },
      };
    });

    let menuUpdated = {
      ...cachedMenu,
    };

    if (categoryHasChanged) {
      menuUpdated = {
        ...menuUpdated,
        items: itemsUpdated,
        modifierGroups: modifiersUpdated,
        structure: cachedMenu.structure.map((category) => {
          if (category.id === previousCategoryId) {
            // remove item from this category
            return {
              ...category,
              childrenEntities: category.childrenEntities.filter(
                (entityItem) => entityItem.id !== item.itemId,
              ),
              status: updateStatus(category.status),
            };
          }
          if (category.id === item.categoryId) {
            // add item to this category
            const movedItemEntity: Entity = {
              name: item.name.displayName,
              id: item.itemId,
              type: 'ITEM',
              status: 'UPDATED',
              childrenEntities: item.childrenEntities,
            };
            return {
              ...category,
              childrenEntities: [...category.childrenEntities, movedItemEntity],
              status: updateStatus(category.status),
            };
          }
          return category;
        }),
        hasChanges: true,
      };
    } else {
      menuUpdated = {
        ...menuUpdated,
        items: itemsUpdated,
        modifierGroups: modifiersUpdated,
        structure: cachedMenu.structure.map((category) =>
          category.id === item.categoryId
            ? {
                ...category,
                childrenEntities: category.childrenEntities.map((entityItem) =>
                  entityItem.id === item.itemId
                    ? {
                        name: item.name.displayName,
                        id: item.itemId,
                        type: 'ITEM',
                        childrenEntities: item.childrenEntities,
                        status: updateStatus(entityItem.status),
                      }
                    : entityItem,
                ),
                status: updateStatus(category.status),
              }
            : category,
        ),
        hasChanges: true,
      };
    }
    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};
type MoveItemProps = {
  brandId: string;
  categoryId: string;
  from: number;
  to: number;
};
export const moveItem = ({ brandId, categoryId, from, to }: MoveItemProps) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);
  if (cachedMenu) {
    const updatedStructure = cachedMenu.structure.map((category) => {
      if (category.id === categoryId) {
        return {
          ...category,
          childrenEntities: move({
            arr: category.childrenEntities,
            from,
            to,
            isMutate: false,
          }),
          status: updateStatus(category.status),
        };
      }
      return category;
    });
    queryCache.setQueryData(menuQuery, {
      ...cachedMenu,
      structure: updatedStructure,
      hasChanges: true,
    });
  }
};
export const removeItem = (
  brandId: string,
  itemId: string,
  categoryId: string,
) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const updatedItems = Object.keys(cachedMenu.items).reduce((object, key) => {
      if (key !== itemId) {
        return {
          ...object,
          [key]: cachedMenu.items[key],
        };
      }
      return object;
    }, {});
    const menuUpdated = {
      ...cachedMenu,
      items: updatedItems,
      structure: cachedMenu.structure.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              childrenEntities: category.childrenEntities.filter(
                (item) => item.id !== itemId,
              ),
              status: updateStatus(category.status),
            }
          : category,
      ),
      hasChanges: true,
    };
    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};

type ModifierModalProps = {
  brandId: string;
  modifier: Partial<ModalModifier>;
};
export const openModifierModal = ({
  brandId,
  modifier,
}: ModifierModalProps) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const menuUpdated = {
      ...cachedMenu,
      modal: {
        modifier: {
          previousData: {
            ...modifier,
            temporalItems: (modifier.items || []).map((itemId) => ({
              id: itemId,
              name: cachedMenu.items[itemId].name.displayName,
              price: cachedMenu.items[itemId].price,
              status: cachedMenu.items[itemId].status,
            })),
          },
        },
      },
    };

    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};
export const addModifier = (brandId: string, modifier: ModalModifier) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    let updatedItems = { ...cachedMenu.items };
    modifier.temporalItems.forEach((item) => {
      updatedItems = {
        ...updatedItems,
        [item.id]: {
          description: '',
          printDescription: '',
          itemId: item.id,
          name: {
            printName: item.name,
            displayName: item.name,
          },
          price: item.price,
          status: item.status,
          state: item.state,
          imageUrl: item.imageUrl,
          imageUrlSmall: item.imageUrlSmall,
          imageUrlMedium: item.imageUrlMedium,
        },
      };
    });

    const updatedStructure = cachedMenu.structure.map((category) => ({
      ...category,
      childrenEntities: category.childrenEntities.map((item) => {
        if (modifier.usedIn.includes(item.id)) {
          return {
            ...item,
            childrenEntities: [
              ...item.childrenEntities,
              {
                name: modifier.name,
                id: modifier.id,
                type: 'MODIFIER_GROUP',
                childrenEntities: [],
                status: 'NEW',
              },
            ],
            status: updateStatus(item.status),
          };
        }
        return item;
      }),
      status: updateStatus(category.status),
    }));

    const menuUpdated = {
      ...cachedMenu,
      structure: updatedStructure,
      items: updatedItems,
      modifierGroups: {
        ...cachedMenu.modifierGroups,
        [modifier.id]: {
          name: modifier.name,
          usedIn: modifier.usedIn,
          id: modifier.id,
          choices: {
            maximum: modifier.choices.maximum,
            minimum: modifier.choices.minimum,
          },
          items: modifier.temporalItems.map((item) => item.id),
        },
      },
      hasChanges: true,
    };

    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};
export const editModifier = (brandId: string, modifier: ModalModifier) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const previousModifierData = cachedMenu.modifierGroups[modifier.id];

    const previousItemIds = [...previousModifierData.items];
    const updatedItemIds = modifier.temporalItems.map((item) => item.id);
    const itemsToAdd = difference(updatedItemIds, previousItemIds);
    const itemsToDelete = difference(previousItemIds, updatedItemIds);

    let updatedItems = Object.keys(cachedMenu.items).reduce((acum, key) => {
      if (itemsToDelete.includes(key)) {
        return acum;
      }
      const itemToUpdate = modifier.temporalItems.find(
        (item) => item.id === key,
      );

      if (itemToUpdate) {
        return {
          ...acum,
          [key]: {
            ...cachedMenu.items[key],
            name: {
              printName: itemToUpdate?.name,
              displayName: itemToUpdate?.name,
            },
            itemId: itemToUpdate?.id,
            price: itemToUpdate?.price,
            status: itemToUpdate?.status,
          },
        };
      }

      return {
        ...acum,
        [key]: cachedMenu.items[key],
      };
    }, {});
    itemsToAdd.forEach((itemId) => {
      const itemToAdd = modifier.temporalItems.find(
        (item) => item.id === itemId,
      );
      updatedItems = {
        ...updatedItems,
        [itemId]: {
          name: {
            printName: itemToAdd?.name,
            displayName: itemToAdd?.name,
          },
          itemId: itemToAdd?.id,
          price: itemToAdd?.price,
          status: itemToAdd?.status,
          state: itemToAdd?.state,
          imageUrl: itemToAdd?.imageUrl,
          imageUrlSmall: itemToAdd?.imageUrlSmall,
          imageUrlMedium: itemToAdd?.imageUrlMedium,
        },
      };
    });

    const newUsedIn = difference(modifier.usedIn, previousModifierData.usedIn);
    const removedUsedIn = difference(
      previousModifierData.usedIn,
      modifier.usedIn,
    );

    const updatedStructure = cachedMenu.structure.map((category) => ({
      ...category,
      childrenEntities: category.childrenEntities.map((item) => {
        if (newUsedIn.includes(item.id)) {
          return {
            ...item,
            childrenEntities: [
              ...item.childrenEntities,
              {
                name: modifier.name,
                id: modifier.id,
                type: 'MODIFIER_GROUP',
                childrenEntities: [],
                status: 'UPDATED',
              },
            ],
            status: updateStatus(item.status),
          };
        }
        if (removedUsedIn.includes(item.id)) {
          return {
            ...item,
            childrenEntities: item.childrenEntities.filter(
              (childrenModifier) => childrenModifier.id !== modifier.id,
            ),
            status: updateStatus(item.status),
          };
        }
        return item;
      }),
      status: updateStatus(category.status),
    }));

    const menuUpdated = {
      ...cachedMenu,
      structure: updatedStructure,
      items: updatedItems,
      modifierGroups: {
        ...cachedMenu.modifierGroups,
        [modifier.id]: {
          ...previousModifierData,
          name: modifier.name,
          usedIn: modifier.usedIn,
          id: modifier.id,
          choices: {
            maximum: modifier.choices.maximum,
            minimum: modifier.choices.minimum,
          },
          items: modifier.temporalItems.map((item) => item.id),
        },
      },
      hasChanges: true,
    };

    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};
export const removeModifier = (brandId: string, modifierIdToDelete: string) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const { updatedModifierGroups, modifierDeleted } = Object.keys(
      cachedMenu.modifierGroups,
    ).reduce<{
      updatedModifierGroups: ModifiersGroup;
      modifierDeleted?: Partial<ModalModifier>;
    }>(
      (acum, modifierId) => {
        if (modifierId === modifierIdToDelete) {
          return {
            ...acum,
            modifierDeleted: cachedMenu.modifierGroups[modifierId],
          };
        }
        return {
          ...acum,
          updatedModifierGroups: {
            ...acum.updatedModifierGroups,
            [modifierId]: cachedMenu.modifierGroups[modifierId],
          },
        };
      },
      {
        updatedModifierGroups: {},
        modifierDeleted: undefined,
      },
    );
    const updatedItems = Object.keys(cachedMenu.items).reduce((object, key) => {
      if (!(modifierDeleted?.items || []).includes(key)) {
        return {
          ...object,
          [key]: cachedMenu.items[key],
        };
      }
      return object;
    }, {});

    const updatedStructure = cachedMenu.structure.map((structure) => ({
      ...structure,
      childrenEntities: structure.childrenEntities.map((item) => ({
        ...item,
        childrenEntities: item.childrenEntities.filter(
          (modifier) => modifier.id !== modifierIdToDelete,
        ),
      })),
    }));

    const menuUpdated = {
      ...cachedMenu,
      structure: updatedStructure,
      items: updatedItems,
      modifierGroups: updatedModifierGroups,
      hasChanges: true,
    };

    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};

export const closeModals = (brandId: string) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const menuUpdated = {
      ...cachedMenu,
      modal: {
        category: undefined,
        item: undefined,
        modifier: undefined,
      },
    };

    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};

export const updateMenuStatus = (brandId: string, status: MenuStatus) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const menuUpdated = {
      ...cachedMenu,
      status,
      hasChanges: false,
    };

    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};

export const getAvailableItems = (brandId: string) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const options = cachedMenu.structure
      .flatMap((category) => category.childrenEntities)
      .map((item) => ({
        label: item.name,
        value: item.id,
      }));

    return {
      options,
      items: cachedMenu.items,
    };
  }

  return {
    options: [],
    items: {},
  };
};

// If you want update only the menu.item object
// Use updateItem method if you need update items in the structure object
export type UpdateCachedItem = {
  brandId: string;
  itemId: string;
  dataToUpdate: Partial<Item>;
};
export const updateCachedItem = ({
  brandId,
  itemId,
  dataToUpdate,
}: UpdateCachedItem) => {
  const menuQuery = ['menu', brandId];
  const cachedMenu = queryCache.getQueryData<MenuLocal>(menuQuery);

  if (cachedMenu) {
    const itemsUpdated = {
      ...cachedMenu.items,
      [itemId]: {
        ...cachedMenu.items[itemId],
        ...dataToUpdate,
      },
    };

    const menuUpdated = {
      ...cachedMenu,
      items: itemsUpdated,
    };

    queryCache.setQueryData(menuQuery, menuUpdated);
  }
};
